import styled, { css } from "styled-components";
import { colors, breakpoints } from "definitions";
import { Link } from "wouter";

/* ---------------------------------------------------------------------------
   Botones
   ---------------------------------------------------------------------------
   `Button`, `YellowAnchorButton`, `BackToAnchorButton` y `BackToLinkButton`
   repetían el mismo bloque con el azul escrito a mano, así que en una barra de
   acciones todos pesaban igual y no se leía cuál era la acción principal. Acá
   hay una sola base y cuatro variantes: primario (relleno), secundario
   (borde), texto y peligro.
--------------------------------------------------------------------------- */

const base = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  box-sizing: border-box;
  min-height: 4rem;
  padding: 0.8rem 1.8rem;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: Lato, sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 2rem;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;

  :focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 110, 223, 0.35);
  }

  svg {
    flex: 0 0 auto;
    width: 1.7rem;
    height: 1.7rem;
  }
`;

const filled = css`
  ${base};
  background-color: ${colors.background.yellowGreen};
  color: ${colors.fonts.white};

  :hover:enabled,
  a&:hover {
    background-color: ${colors.icons.darkBlue};
  }

  :disabled {
    background-color: #b8c4d4;
    color: ${colors.fonts.white};
    cursor: not-allowed;
  }
`;

const outlined = css`
  ${base};
  background-color: transparent;
  border-color: #bcd6f7;
  color: ${colors.fonts.yellowGreen};

  :hover:enabled,
  a&:hover {
    background-color: #e8f1fd;
    border-color: ${colors.icons.yellowGreen};
  }

  /* El atributo cubre a los que se renderizan como <a>, donde :disabled
     no aplica (por ejemplo el "Verificar" del video). */
  :disabled,
  &[disabled] {
    border-color: #dde3ee;
    color: #9aa5b5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const quiet = css`
  ${base};
  padding: 0.8rem 1rem;
  background-color: transparent;
  color: ${colors.fonts.darkGray};

  :hover:enabled,
  a&:hover {
    color: ${colors.fonts.yellowGreen};
  }

  :disabled {
    color: #9aa5b5;
    cursor: not-allowed;
  }
`;

/* Primario: la acción principal de cada pantalla. */
export const Button = styled.button`
  ${filled};
`;

/* Secundario: acompaña al primario sin competirle. */
export const SecondaryButton = styled.button`
  ${outlined};
`;

/* Terciario: navegación y acciones de bajo peso (imprimir, volver). */
export const QuietButton = styled.button`
  ${quiet};
`;

/* Destructivo: descartar la postulación. */
export const RedButton = styled.button`
  ${base};
  background-color: transparent;
  border-color: #f0c1c1;
  color: ${colors.background.red};
  width: ${({ width }) => width || "auto"};

  :hover:enabled {
    background-color: #fdeaea;
    border-color: ${colors.background.red};
  }

  :disabled {
    border-color: #dde3ee;
    color: #9aa5b5;
    cursor: not-allowed;
  }
`;

export const YellowButton = styled.button`
  ${filled};
`;

export const WhiteButton = styled.button`
  ${base};
  background-color: ${colors.background.white};
  color: ${colors.fonts.darkGray};

  :hover:enabled {
    background-color: ${colors.background.veryLightGray};
  }

  :disabled {
    background-color: #b8c4d4;
    color: ${colors.fonts.white};
    cursor: not-allowed;
  }
`;

export const MoreButton = styled.button`
  ${filled};
  height: 6.5rem;
  font-weight: 700;
  font-size: 2.4rem;
  text-transform: capitalize;

  width: 95%;
  max-width: 124.5rem;

  ${breakpoints.tabletXL} {
    width: 80%;
  }
`;

export const WhiteLargeButton = styled.button`
  ${base};
  width: 100%;
  min-height: 4.8rem;
  border-radius: 6px;
  background: ${colors.background.white};
  color: ${colors.fonts.darkGray};
  font-size: 1.6rem;
`;

export const YellowAnchorButton = styled.a`
  ${filled};
`;

/* Volver: es navegación, no una acción del formulario. */
export const BackToAnchorButton = styled.a`
  ${quiet};
`;

export const BackToLinkButton = styled(Link)`
  ${quiet};
`;

export const CancelButton = styled.button`
  ${base};
  background-color: #f6f7f9;
  color: ${colors.background.red};
  border-color: ${colors.background.red};
  width: ${(props) => (props.width === "40%" ? "40%" : "auto")};

  :hover:enabled {
    background-color: ${colors.background.veryLightGray};
  }
`;

export const DownloadLink = styled.a`
  ${outlined};

  ${breakpoints.tabletXL} {
    align-self: center;
  }
`;

/* Confirmar: es el primario del panel que lo contiene, así que usa el mismo
   azul que el resto de los botones principales (antes iba con el azul
   eléctrico del hero y no pegaba con nada alrededor). La variante `cancel`
   —paneles que confirman algo destructivo— sigue en rojo. */
export const ConfirmButton = styled.button`
  ${filled};

  ${({ type }) =>
    type === "cancel" &&
    css`
      background-color: ${colors.background.red};

      :hover:enabled {
        background-color: ${colors.hover.red};
      }
    `};
`;

export const ExcelButton = styled.button`
  ${base};
  background-color: #1d6f42;
  color: ${colors.fonts.white};

  :hover:enabled {
    background-color: #5b9e48;
  }

  :disabled {
    background-color: #5b9e48;
    cursor: not-allowed;
  }
`;

export const CleanButton = styled.button`
  ${outlined};
  margin: 0 1.5rem;

  ${breakpoints.laptop} {
    margin: 0;
  }
`;
